import { Controller } from "@hotwired/stimulus"

// Stacking undo toasts. Net-new infrastructure — Service and POS will mount
// this same controller later, so nothing here is kitchen-specific.
//
// Event contract for whatever wires an undoable action (Task 10's Done
// button is the first caller):
//
//   document.dispatchEvent(new CustomEvent("ops-toasts:show", {
//     detail: {
//       label: "A41 marked done",  // Names the ticket. Three stacked
//                                   // "marked done" toasts with no
//                                   // reference would be worse than none.
//       actionLabel: "Undo",
//       url: actionUrl             // POSTed to on click — the same
//                                   // resource_url_for(ticket, interaction:,
//                                   // format: :json) helper the ticket
//                                   // partial already builds action_url
//                                   // from works here unchanged.
//     }
//   }))
//
// Turbo: the element this controller mounts on carries data-turbo-permanent
// and a stable id (see _toasts.html.erb). The ops layout sets
// turbo-refresh-method: morph and boards receive broadcast_refresh_to pings
// continuously — without turbo-permanent, a refresh landing a second after
// the tap would morph this container back to the empty div the server
// renders and wipe the only way to undo. turbo-permanent also means this
// exact node (and this controller instance) survives a normal Drive visit
// between ops screens, so the listener below is armed once per tab rather
// than needing to be re-armed on every navigation.
const MAX_VISIBLE = 3
const LIFETIME_MS = 30000
// A stack that reflows under a moving thumb is how the wrong thing gets
// undone: the slot holds its height briefly before collapsing.
const COLLAPSE_DELAY_MS = 400

export default class extends Controller {
  connect() {
    this.boundShow = this.show.bind(this)
    document.addEventListener("ops-toasts:show", this.boundShow)
    // Re-arm anything already on screen. The container is turbo-permanent, so
    // toasts outlive a reconnect but their timers do not — disconnect clears
    // them and without this they were never rearmed, leaving an undo prompt
    // sitting on the board forever. Ops boards morph-refresh constantly, so
    // this fired on essentially every toast.
    this.element.querySelectorAll("[data-toast]").forEach((t) => this.#arm(t))
  }

  disconnect() {
    document.removeEventListener("ops-toasts:show", this.boundShow)
    this.element.querySelectorAll("[data-toast]").forEach((t) => this.#clearTimer(t))
  }

  // Expiry is stored on the element rather than only in the closure, so it
  // survives the controller instance being torn down and rebuilt.
  #arm(toast) {
    this.#clearTimer(toast)
    const expiresAt = Number(toast.dataset.expiresAt)
    const remaining = expiresAt - Date.now()

    if (remaining <= 0) return this.#dismiss(toast)

    toast._timer = setTimeout(() => this.#dismiss(toast), remaining)
  }

  show(event) {
    const { label, actionLabel, url } = event.detail
    const toast = this.#build(label, actionLabel, url)

    toast.dataset.expiresAt = String(Date.now() + LIFETIME_MS)
    this.element.appendChild(toast)
    this.#enforceLimit()
    this.#arm(toast)
  }

  #build(label, actionLabel, url) {
    const toast = document.createElement("div")
    toast.dataset.toast = "true"
    toast.className =
      "pointer-events-auto flex items-center gap-4 rounded px-4 py-3 shadow-lg " +
      "transition-opacity duration-200"
    toast.style.background = "var(--pu-surface-raised)"
    toast.style.color = "var(--pu-text)"
    toast.style.minWidth = "260px"

    const text = document.createElement("span")
    text.className = "text-sm"
    text.textContent = label

    const action = document.createElement("button")
    action.type = "button"
    action.className = "ml-auto text-sm font-bold"
    action.style.color = "var(--ops-state-ready)"
    action.textContent = actionLabel
    action.addEventListener("click", () => {
      fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          Accept: "application/json"
        }
      }).finally(() => this.#dismiss(toast))
    })

    toast.append(text, action)
    return toast
  }

  #enforceLimit() {
    const toasts = [...this.element.querySelectorAll("[data-toast]")]
    while (toasts.length > MAX_VISIBLE) {
      this.#dismiss(toasts.shift())
    }
  }

  #dismiss(toast) {
    if (!toast || toast.dataset.dismissing) return
    toast.dataset.dismissing = "true"
    this.#clearTimer(toast)

    toast.style.opacity = "0"
    toast.style.height = `${toast.offsetHeight}px`
    setTimeout(() => toast.remove(), COLLAPSE_DELAY_MS)
  }

  #clearTimer(toast) {
    if (toast._timer) clearTimeout(toast._timer)
  }
}
